import { alpha, type Theme } from "@mui/material/styles";

import { AuctionState } from "@/src/types/types";
import {
  type CardTypeFlags,
  isSuccessState,
  isWarningState
} from "@/src/utils/auction/auctionState";

export type VehicleInfoChipTone =
  | "lightGreen"
  | "lightYellow"
  | "lightRed"
  | "lightGrey";

export const getVehicleInfoChipBackground = (
  theme: Theme,
  tone: VehicleInfoChipTone
): string => {
  switch (tone) {
    case "lightGreen":
      return alpha(theme.palette.semantic.success.light, 0.5);
    case "lightYellow":
      return alpha(theme.palette.misc.ratingActive, 0.45);
    case "lightRed":
      return alpha(theme.palette.semantic.error.light, 0.5);
    case "lightGrey":
      return theme.palette.action.selected;
  }
};

export const getAuctionStateMainColor = (
  theme: Theme,
  state: AuctionState,
  hasPendingOffer: boolean
): string => {
  if (state === AuctionState.STARTED) {
    return theme.palette.semantic.primary.main;
  }
  if (isWarningState(state, hasPendingOffer)) {
    return theme.palette.semantic.warning.main;
  }
  if (isSuccessState(state)) {
    return theme.palette.semantic.success.main;
  }
  if (
    state === AuctionState.NOT_SOLD ||
    state === AuctionState.REJECTED_BY_DEALER
  ) {
    return theme.palette.text.secondary;
  }
  return theme.palette.semantic.info.enabled50p;
};

export const getAuctionStateLightBackground = (
  theme: Theme,
  state: AuctionState,
  hasPendingOffer: boolean,
  hasFinalOffer?: boolean
): string => {
  if (hasFinalOffer) {
    return theme.palette.background.paper;
  }
  if (isWarningState(state, hasPendingOffer)) {
    return theme.palette.semantic.warning.selected;
  }
  if (state === AuctionState.STARTED) {
    return theme.palette.background.paper;
  }
  if (isSuccessState(state)) {
    return theme.palette.semantic.success.selected;
  }
  if (
    state === AuctionState.NOT_SOLD ||
    state === AuctionState.REJECTED_BY_DEALER
  ) {
    return theme.palette.semantic.secondary.selected;
  }
  return theme.palette.background.paper;
};

export const getAuctionHoverBorderColor = (
  theme: Theme,
  state: AuctionState,
  flags: CardTypeFlags
): string => {
  if (state === AuctionState.STARTED) {
    return theme.palette.semantic.primary.main;
  }
  if (flags.isFinalOfferCard || flags.isWarningCard) {
    return theme.palette.semantic.warning.main;
  }
  if (flags.isSuccessCard) {
    return theme.palette.semantic.success.main;
  }
  if (flags.isNotSoldCard) {
    return theme.palette.text.secondary;
  }
  return theme.palette.semantic.info.enabled50p;
};

const PULSE_ANIMATION = "pulse 2s ease-in-out infinite";

/**
 * Compact chip styles for list/header chips (paper background, thin border).
 */
export const getAuctionStatusChipStyles = (
  theme: Theme,
  state: AuctionState,
  hasPendingOffer: boolean
) => {
  const warningStatusColor = {
    bgcolor: theme.palette.background.paper,
    color: theme.palette.semantic.warning.dark,
    border: `1px solid ${theme.palette.semantic.warning.main}`
  };

  const primaryStatusColor = {
    bgcolor: theme.palette.background.paper,
    color: theme.palette.semantic.primary.main,
    border: `1px solid ${theme.palette.semantic.primary.main}`
  };

  if (state === AuctionState.ENDED && hasPendingOffer) {
    return warningStatusColor;
  }

  switch (state) {
    case AuctionState.STARTED:
      return primaryStatusColor;
    case AuctionState.DRAFT:
      return warningStatusColor;
    case AuctionState.OFFER_ACCEPTED:
    case AuctionState.SOLD:
    case AuctionState.SENT_FOR_BILLING:
    case AuctionState.BILLED:
      return {
        bgcolor: theme.palette.background.paper,
        color: theme.palette.semantic.success.main,
        border: `1px solid ${theme.palette.semantic.success.main}`
      };
    case AuctionState.NOT_SOLD:
    case AuctionState.REJECTED_BY_DEALER:
      return {
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.text.secondary}`
      };
    case AuctionState.CANCELLED:
    case AuctionState.DELETED:
    case AuctionState.ENDED:
    default:
      return warningStatusColor;
  }
};

export interface AuctionStatusChipConfig {
  /** i18n key — resolve with `t(labelKey, labelFallback)` in the caller. */
  labelKey: string;
  /** Fallback when the translation is missing (usually the raw state). */
  labelFallback?: string;
  backgroundColor: string;
  textColor: string;
  /** Full CSS `border` value (e.g. `"2px solid …"` or `"none"`). */
  border: string;
  animation: string;
}

/**
 * Rich chip styles for consumer auction cards (tinted background, pulse).
 * Callers own label translation via `labelKey` / `labelFallback`.
 */
export const getAuctionStatusChipConfig = (
  theme: Theme,
  state: AuctionState,
  hasPendingOffer: boolean,
  hasNegotiationMessages: boolean
): AuctionStatusChipConfig => {
  let labelKey = `auctionStates.seller.${state}`;
  let labelFallback: string | undefined = state;
  let backgroundColor = theme.palette.common.white;
  let textColor = theme.palette.text.primary;
  let border = "none";
  let animation = "none";

  if (state === AuctionState.ENDED && hasPendingOffer) {
    return {
      labelKey: hasNegotiationMessages
        ? "myAuctions.inNegotiation"
        : "myAuctions.pendingOffer",
      textColor: theme.palette.semantic.warning.dark,
      backgroundColor: theme.palette.semantic.warning.light,
      border: `2px solid ${theme.palette.semantic.warning.main}`,
      animation: PULSE_ANIMATION
    };
  }

  switch (state) {
    case AuctionState.STARTED:
      labelKey = "auctionStates.seller.STARTED";
      labelFallback = undefined;
      textColor = theme.palette.semantic.info.dark;
      backgroundColor = theme.palette.semantic.info.light;
      border = `2px solid ${theme.palette.semantic.info.main}`;
      animation = PULSE_ANIMATION;
      break;
    case AuctionState.DRAFT:
      labelKey = "auctionStates.seller.DRAFT";
      labelFallback = undefined;
      textColor = theme.palette.semantic.warning.dark;
      backgroundColor = theme.palette.semantic.warning.light;
      break;
    case AuctionState.OFFER_ACCEPTED:
      labelKey = "auctionStates.seller.OFFER_ACCEPTED";
      labelFallback = undefined;
      textColor = theme.palette.semantic.success.dark;
      backgroundColor = theme.palette.semantic.success.light;
      border = `2px solid ${theme.palette.semantic.success.main}`;
      animation = PULSE_ANIMATION;
      break;
    case AuctionState.SOLD:
    case AuctionState.SENT_FOR_BILLING:
    case AuctionState.BILLED:
      labelKey = "auctionStates.seller.SOLD";
      labelFallback = undefined;
      textColor = theme.palette.semantic.success.dark;
      backgroundColor = theme.palette.semantic.success.light;
      border = `2px solid ${theme.palette.semantic.success.main}`;
      animation = PULSE_ANIMATION;
      break;
    case AuctionState.ENDED:
      labelKey = "auctionStates.seller.ENDED";
      labelFallback = undefined;
      textColor = theme.palette.text.secondary;
      backgroundColor = theme.palette.misc.timelineBackground;
      break;
    case AuctionState.CANCELLED:
    case AuctionState.DELETED:
    case AuctionState.REJECTED_BY_DEALER:
      labelKey = `auctionStates.seller.${state}`;
      labelFallback = state;
      textColor = theme.palette.semantic.error.dark;
      backgroundColor = theme.palette.semantic.error.light;
      border = `2px solid ${theme.palette.semantic.error.main}`;
      break;
  }

  return {
    labelKey,
    labelFallback,
    backgroundColor,
    textColor,
    border,
    animation
  };
};

export const getAuctionTimerTextColor = (theme: Theme): string =>
  theme.palette.semantic.warning.main;

export const getAuctionCardShadow = (
  theme: Theme,
  flags: CardTypeFlags,
  showAccent: boolean
): string => {
  if (
    flags.isWarningCard ||
    flags.isFinalOfferCard ||
    flags.isSuccessCard ||
    flags.isNotSoldCard
  ) {
    return `0 0 7px 0 ${theme.palette.action.disabled}`;
  }
  if (showAccent) {
    return `0 2px 8px ${theme.palette.action.selected}`;
  }
  return `0 1px 3px ${theme.palette.action.selected}`;
};

export const getAuctionCardHoverShadow = (
  theme: Theme,
  flags: CardTypeFlags
): string => {
  if (
    flags.isWarningCard ||
    flags.isFinalOfferCard ||
    flags.isSuccessCard ||
    flags.isNotSoldCard
  ) {
    return `0 0 7px 0 ${theme.palette.misc.outlineBorder}`;
  }
  return `0 4px 12px ${theme.palette.divider}`;
};
